import * as R from "ramda";
import React, { useContext } from "react";
import ReactPlayer from "react-player";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Collapse,
  IconButton,
  MobileStepper,
  Typography,
} from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import StyledComponent from "styled-components";
import SwipeableViews from "react-swipeable-views";
import { LanguageContext } from "contexts/language.context";
import { Translate } from "utils/translate";
import VideoPlayer from "components/Players/VideoPlayer";

const AutoPlaySwipeableViews = SwipeableViews;

const Gutter = StyledComponent.div`
  padding-left: 1.25rem;
`;

const ProgramNote = StyledComponent(CardContent)`
  padding-top: 0 !important;
  padding-left: 2rem !important;
  text-align: left;
`;

const PerformanceHistory = StyledComponent(CardContent)`
  padding-top: 0 !important;
  padding-left: 3rem !important;
  text-align: left;
`;

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const GenerateMedia = ({ videos, audio }) => {
  return {
    media: [
      ...(videos || []).map((video, index) => (
        <VideoPlayer key={`videoplayer-${index}`} {...video} />
      )),
      ...(audio || []).map(({ url }, index) => (
        <ReactPlayer key={`audioplayer-${index}`} url={url} />
      )),
    ],
    maxSteps: (audio || []).length + (videos || []).length,
  };
};

const WorkCard = ({
  name,
  description,
  comments,
  videos,
  audio,
  programNote,
  performanceHistory,
}) => {
  const { language } = useContext(LanguageContext);

  const [activeStep, setActiveStep] = React.useState(0);
  const [programNotesExpanded, setProgramNotesExpanded] = React.useState(false);
  const [performanceHistoryExpanded, setPerformanceHistoryExpanded] =
    React.useState(false);

  const handleExpandProgramNotesClick = () => {
    setProgramNotesExpanded(!programNotesExpanded);
  };
  const handleExpandPerformanceHistoryClick = () => {
    setPerformanceHistoryExpanded(!performanceHistoryExpanded);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  const { maxSteps, media } = GenerateMedia({ videos, audio });

  return (
    <Card sx={{ marginBottom: "0.5rem" }}>
      <CardContent>
        <Typography variant="h6">{name}</Typography>
        <Typography variant="body2">{description}</Typography>
        <Gutter>{comments}</Gutter>
      </CardContent>

      {maxSteps > 1 ? (
        <>
          <AutoPlaySwipeableViews
            axis={"x"}
            index={activeStep}
            onChangeIndex={handleStepChange}
            enableMouseEvents
          >
            {media.map((step, index) => (
              <div key={`media-${index}`}>
                {Math.abs(activeStep - index) <= 2 ? step : null}
              </div>
            ))}
          </AutoPlaySwipeableViews>

          <MobileStepper
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            nextButton={
              <Button
                size="small"
                onClick={handleNext}
                disabled={activeStep === maxSteps - 1}
              >
                {Translate(language, ["WORK", "next"])}
                <KeyboardArrowRight />
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                <KeyboardArrowLeft />
                {Translate(language, ["WORK", "back"])}
              </Button>
            }
          />
        </>
      ) : null}
      {programNote || R.isEmpty(programNote) ? (
        <>
          <CardActions disableSpacing>
            <Typography variant="body2" sx={{ paddingLeft: "1rem" }}>
              {Translate(language, ["WORK", "programNote"])}
            </Typography>
            <ExpandMore
              expand={programNotesExpanded}
              onClick={handleExpandProgramNotesClick}
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </CardActions>

          <Collapse in={programNotesExpanded} timeout="auto" unmountOnExit>
            <ProgramNote>
              {programNote.map((text, index) => (
                <Typography
                  variant="caption"
                  key={`programNote-${name}-${index}`}
                >
                  {text}
                </Typography>
              ))}
            </ProgramNote>
          </Collapse>
        </>
      ) : null}
      {performanceHistory || R.isEmpty(performanceHistory) ? (
        <>
          <CardActions disableSpacing>
            <Typography variant="body2" sx={{ paddingLeft: "1rem" }}>
              {Translate(language, ["WORK", "performanceHistory"])}
            </Typography>
            <ExpandMore
              expand={performanceHistoryExpanded}
              onClick={handleExpandPerformanceHistoryClick}
            >
              <ExpandMoreIcon />
            </ExpandMore>
          </CardActions>

          <Collapse
            in={performanceHistoryExpanded}
            timeout="auto"
            unmountOnExit
          >
            <PerformanceHistory>
              <ul>
                {performanceHistory.map((text, index) => (
                  <li>
                    <Typography
                      variant="caption"
                      key={`performancehistory-${name}-${index}`}
                    >
                      {text}
                    </Typography>
                  </li>
                ))}
              </ul>
            </PerformanceHistory>
          </Collapse>
        </>
      ) : null}
    </Card>
  );
};

export default WorkCard;
